import { z } from 'zod';
import {
  GARMENT_CATEGORIES,
  HYPE_KINDS,
  MOOD_IDS,
  OCCASION_IDS,
} from './moods.js';

/* ------------------------------------------------------------------ *
 * Primitives
 * ------------------------------------------------------------------ */

export const zMoodId = z.enum(MOOD_IDS);
export const zHypeKind = z.enum(HYPE_KINDS);
export const zOccasionId = z.enum(OCCASION_IDS);
export const zGarmentCategory = z.enum(GARMENT_CATEGORIES);

export const zId = z.string().min(1).max(64);
export const zTimestamp = z.number().int().nonnegative();

export const zUsername = z
  .string()
  .min(3, 'at least 3 characters')
  .max(24, 'at most 24 characters')
  .regex(/^[a-z0-9_.]+$/, 'lowercase letters, numbers, _ and . only');

export const zEmail = z.string().email('that email looks off').max(254);

/** Quoted in the sign-up hint on every platform, so it is exported, not typed out. */
export const PASSWORD_MIN_LENGTH = 10;

export const zPassword = z
  .string()
  .min(PASSWORD_MIN_LENGTH, `at least ${PASSWORD_MIN_LENGTH} characters`)
  .max(200, 'that is plenty long')
  .regex(/[a-zA-Z]/, 'needs at least one letter')
  .regex(/[0-9!@#$%^&*()\-_=+[\]{};:,.<>?]/, 'needs a number or symbol');

export const zVisibility = z.enum(['private', 'friends', 'room', 'public']);
export type Visibility = z.infer<typeof zVisibility>;

export const zPlan = z.enum(['free', 'muse', 'studio']);
export type Plan = z.infer<typeof zPlan>;

/* ------------------------------------------------------------------ *
 * User
 * ------------------------------------------------------------------ */

export const zUser = z.object({
  id: zId,
  username: zUsername,
  displayName: z.string().min(1).max(60),
  email: zEmail,
  avatarUrl: z.string().url().nullable(),
  bio: z.string().max(240).default(''),
  /** The mood the user identifies with right now — shown as an aura on their profile. */
  signatureMood: zMoodId.nullable(),
  /** Free-text era label, e.g. "villain era", "soft reset". Purely narrative. */
  era: z.string().max(40).nullable(),
  links: z.array(z.object({ label: z.string().max(30), url: z.string().url() })).max(5).default([]),
  plan: zPlan.default('free'),
  /** Purchased/granted fittings. Paid plans spend these; free users do not. */
  credits: z.number().int().nonnegative().default(0),
  /**
   * Fittings the user can still start today. Server-computed on every read.
   * Free plans draw from a daily allowance, not from `credits`, so this is the
   * only field a client should ever show as "fittings left". `-1` means
   * unlimited (paid plans).
   */
  fittingsRemaining: z.number().int().optional(),
  isCreator: z.boolean().default(false),
  emailVerified: z.boolean().default(false),
  authProviders: z.array(z.enum(['password', 'google'])).default([]),
  counts: z
    .object({
      looks: z.number().int().nonnegative().default(0),
      followers: z.number().int().nonnegative().default(0),
      following: z.number().int().nonnegative().default(0),
      hypeReceived: z.number().int().nonnegative().default(0),
    })
    .default({ looks: 0, followers: 0, following: 0, hypeReceived: 0 }),
  createdAt: zTimestamp,
  updatedAt: zTimestamp,
});
export type User = z.infer<typeof zUser>;

/** Everything safe to show to another user. */
export const zPublicUser = zUser
  .omit({
    email: true,
    credits: true,
    fittingsRemaining: true,
    plan: true,
    emailVerified: true,
    authProviders: true,
    updatedAt: true,
  });
export type PublicUser = z.infer<typeof zPublicUser>;

/* ------------------------------------------------------------------ *
 * Body shots — the user's canvas
 * ------------------------------------------------------------------ */

export const zBodyShot = z.object({
  id: zId,
  userId: zId,
  url: z.string(),
  thumbUrl: z.string(),
  width: z.number().int().positive(),
  height: z.number().int().positive(),
  /** User-given name, e.g. "hallway mirror", "good lighting day". */
  label: z.string().max(40).default(''),
  isPrimary: z.boolean().default(false),
  /** Quality read produced on upload so we can coach the user kindly. */
  quality: z
    .object({
      score: z.number().min(0).max(100),
      fullBody: z.boolean(),
      notes: z.array(z.string()).max(4),
    })
    .nullable()
    .default(null),
  createdAt: zTimestamp,
});
export type BodyShot = z.infer<typeof zBodyShot>;

/* ------------------------------------------------------------------ *
 * Garments
 * ------------------------------------------------------------------ */

export const zGarment = z.object({
  id: zId,
  slug: z.string(),
  name: z.string().min(1).max(80),
  brand: z.string().max(60).default(''),
  category: zGarmentCategory,
  /**
   * The garment's product photograph. This is the PRIMARY asset — the try-on
   * model conditions on this image, not on the text.
   */
  imageUrl: z.string(),
  thumbUrl: z.string(),
  /**
   * Where `imageUrl` came from:
   *   catalog     — a real product photo (imported from a store feed or uploaded)
   *   generated   — text-seeded stand-in, produced from `description`
   *   placeholder — the generated SVG flat-lay; never sent to the image model
   */
  imageSource: z.enum(['catalog', 'generated', 'placeholder']).optional(),
  /**
   * Caption for the product photo: fabric, cut, hardware, drape. Used as
   * supporting metadata for try-on, as alt text, as search signal, and as the
   * seed prompt when no real photograph exists yet. Where this and the photo
   * disagree, the photo wins.
   */
  description: z.string().max(600).default(''),
  moods: z.array(zMoodId).min(1).max(4),
  occasions: z.array(zOccasionId).max(4).default([]),
  palette: z.array(z.string()).max(6).default([]),
  tags: z.array(z.string().max(24)).max(12).default([]),
  priceCents: z.number().int().nonnegative().nullable().default(null),
  currency: z.string().length(3).default('USD'),
  shopUrl: z.string().url().nullable().default(null),
  dropId: zId.nullable().default(null),
  creatorId: zId.nullable().default(null),
  /** How many times it has been worn in the product — powers "trending". */
  wearCount: z.number().int().nonnegative().default(0),
  featured: z.boolean().default(false),
  /**
   * This piece cannot be rendered decently on its own: it is sheer, it is
   * structurally underwear, it is cut away, or it is an open layer that leaves
   * the torso bare when worn alone. `buildTryOnPrompt` turns this into an
   * explicit demand for an opaque underlayer.
   *
   * Optional on purpose — every garment that predates this flag, and every
   * garment imported from a partner feed, keeps parsing without it. Absent
   * means "no special handling", never "verified safe".
   */
  requiresBaseLayer: z.boolean().optional(),
  createdAt: zTimestamp,
});
export type Garment = z.infer<typeof zGarment>;

/* ------------------------------------------------------------------ *
 * Image safety verdicts
 *
 * The structured answer from a vision pass over an image — either one the
 * user uploaded (input gate) or one we just generated (output gate).
 *
 * The parse helper below is the single place where "is this image ok" is
 * decided, and it FAILS CLOSED: only an unambiguous, well-formed, explicit
 * pass is treated as safe. A garbled verdict, a missing field, a `safe` flag
 * that contradicts its own reason — all of it is unsafe. A blocked legitimate
 * try-on is a bad afternoon; a shipped nude of a real person is not.
 * ------------------------------------------------------------------ */

/** Reasons the model itself is allowed to return. */
export const IMAGE_SAFETY_REASONS = [
  'ok',
  'nudity',
  'partial_nudity',
  'underwear_or_swimwear',
  'minor',
  'no_person',
  'other',
] as const;
export type ModelSafetyReason = (typeof IMAGE_SAFETY_REASONS)[number];

/**
 * Reasons we assign ourselves when the check never produced a usable answer.
 * Every one of them means "unsafe" — that is the whole point of them existing.
 */
export const INTERNAL_SAFETY_REASONS = [
  /** The verdict came back but did not parse against the schema. */
  'unreadable',
  /** The check threw, timed out, or the network died. */
  'check_failed',
  /** Gemini refused to process the image at all. */
  'upstream_blocked',
] as const;
export type InternalSafetyReason = (typeof INTERNAL_SAFETY_REASONS)[number];

export type SafetyReason = ModelSafetyReason | InternalSafetyReason;

export const zImageSafetyVerdict = z.object({
  safe: z.boolean(),
  reason: z.enum(IMAGE_SAFETY_REASONS),
  detail: z.string().max(300).optional(),
});

export interface ImageSafetyVerdict {
  safe: boolean;
  reason: SafetyReason;
  /** Short model-written note. Safe to log; never contains the image. */
  detail: string;
}

/** Every rejection funnels through here so the shape is identical everywhere. */
export function unsafeVerdict(reason: SafetyReason, detail = ''): ImageSafetyVerdict {
  return { safe: false, reason, detail: detail.slice(0, 300) };
}

/**
 * The only function allowed to return `safe: true`.
 *
 * Note there is no coercion anywhere: `"true"` is not `true`, `1` is not
 * `true`, and a missing field is not a default. The model is given a response
 * schema that makes the correct shape trivial to produce, so anything that
 * fails to parse is a signal that the call went sideways — which is exactly
 * when we least want to trust it.
 */
export function parseImageSafetyVerdict(raw: unknown): ImageSafetyVerdict {
  const parsed = zImageSafetyVerdict.safeParse(raw);
  if (!parsed.success) return unsafeVerdict('unreadable');

  const { safe, reason, detail } = parsed.data;

  // A verdict that disagrees with itself is ambiguous, and ambiguous is unsafe.
  // `safe: true, reason: 'nudity'` and `safe: false, reason: 'ok'` are both
  // failures to answer the question, not answers.
  if (safe !== true) return unsafeVerdict(reason === 'ok' ? 'other' : reason, detail);
  if (reason !== 'ok') return unsafeVerdict(reason, detail);

  return { safe: true, reason: 'ok', detail: (detail ?? '').slice(0, 300) };
}

/* ------------------------------------------------------------------ *
 * Mood read — the emotional interpretation of a finished look
 * ------------------------------------------------------------------ */

export const zMoodRead = z.object({
  /** One-line verdict, written to be screenshot-able. */
  verdict: z.string().max(120),
  /** 0-100. Not a score of the person — a read of the outfit's volume. */
  energy: z.number().min(0).max(100),
  moods: z.array(zMoodId).min(1).max(3),
  adjectives: z.array(z.string().max(20)).min(2).max(5),
  colorStory: z.string().max(140),
  /** Why the combination works, in the voice of a friend who knows clothes. */
  whyItWorks: z.string().max(280),
  /** One gentle, optional suggestion. Never a criticism of the body. */
  oneNote: z.string().max(160),
  /** Ready-to-paste caption for the socials. */
  caption: z.string().max(220),
  hashtags: z.array(z.string().max(30)).max(8).default([]),
});
export type MoodRead = z.infer<typeof zMoodRead>;

/* ------------------------------------------------------------------ *
 * Looks
 * ------------------------------------------------------------------ */

export const zLookStats = z.object({
  hype: z.record(zHypeKind, z.number().int().nonnegative()).default({}),
  totalHype: z.number().int().nonnegative().default(0),
  views: z.number().int().nonnegative().default(0),
  saves: z.number().int().nonnegative().default(0),
  remixes: z.number().int().nonnegative().default(0),
});
export type LookStats = z.infer<typeof zLookStats>;

export const zLook = z.object({
  id: zId,
  userId: zId,
  author: zPublicUser.optional(),
  bodyShotId: zId,
  garmentIds: z.array(zId).min(1).max(6),
  garments: z.array(zGarment).optional(),
  url: z.string(),
  thumbUrl: z.string(),
  width: z.number().int().positive().default(1024),
  height: z.number().int().positive().default(1536),
  moodRead: zMoodRead.nullable().default(null),
  /** The mood the user *asked* for, which may differ from what the read returned. */
  intendedMood: zMoodId.nullable().default(null),
  occasion: zOccasionId.nullable().default(null),
  /** Free-text scene the user typed, e.g. "rooftop in lisbon at golden hour". */
  scene: z.string().max(200).default(''),
  title: z.string().max(80).default(''),
  visibility: zVisibility.default('private'),
  roomId: zId.nullable().default(null),
  lookbookIds: z.array(zId).max(20).default([]),
  /** Set when this look was created by remixing someone else's. */
  remixOfId: zId.nullable().default(null),
  stats: zLookStats.default({ hype: {}, totalHype: 0, views: 0, saves: 0, remixes: 0 }),
  /** Hype kinds the requesting viewer has already sent. */
  viewerHype: z.array(zHypeKind).default([]),
  createdAt: zTimestamp,
});
export type Look = z.infer<typeof zLook>;

/* ------------------------------------------------------------------ *
 * Lookbooks — narrative collections ("eras")
 * ------------------------------------------------------------------ */

export const zLookbook = z.object({
  id: zId,
  userId: zId,
  title: z.string().min(1).max(60),
  subtitle: z.string().max(120).default(''),
  coverLookId: zId.nullable().default(null),
  coverUrl: z.string().nullable().default(null),
  lookIds: z.array(zId).max(200).default([]),
  mood: zMoodId.nullable().default(null),
  visibility: zVisibility.default('private'),
  createdAt: zTimestamp,
  updatedAt: zTimestamp,
});
export type Lookbook = z.infer<typeof zLookbook>;

/* ------------------------------------------------------------------ *
 * Fitting rooms — live, social, ephemeral
 * ------------------------------------------------------------------ */

export const zRoomMember = z.object({
  userId: zId,
  username: zUsername,
  displayName: z.string(),
  avatarUrl: z.string().nullable(),
  role: z.enum(['host', 'guest']),
  online: z.boolean().default(false),
  joinedAt: zTimestamp,
});
export type RoomMember = z.infer<typeof zRoomMember>;

export const zRoom = z.object({
  id: zId,
  code: z.string().length(6),
  hostId: zId,
  title: z.string().max(60).default('Fitting room'),
  prompt: z.string().max(140).default(''),
  members: z.array(zRoomMember).default([]),
  lookIds: z.array(zId).default([]),
  /** Verdict tally per look while the room is live. */
  votes: z.record(zId, z.record(zHypeKind, z.number().int())).default({}),
  status: z.enum(['live', 'closed']).default('live'),
  createdAt: zTimestamp,
  expiresAt: zTimestamp,
});
export type Room = z.infer<typeof zRoom>;

/* ------------------------------------------------------------------ *
 * Drops — creator-curated garment collections
 * ------------------------------------------------------------------ */

export const zDrop = z.object({
  id: zId,
  slug: z.string(),
  title: z.string().max(80),
  blurb: z.string().max(200).default(''),
  creatorId: zId.nullable().default(null),
  creator: zPublicUser.nullable().optional(),
  coverUrl: z.string().nullable().default(null),
  garmentIds: z.array(zId).default([]),
  mood: zMoodId.nullable().default(null),
  startsAt: zTimestamp,
  endsAt: zTimestamp.nullable().default(null),
  createdAt: zTimestamp,
});
export type Drop = z.infer<typeof zDrop>;

/* ------------------------------------------------------------------ *
 * Jobs — try-on generation
 * ------------------------------------------------------------------ */

export const zJobStatus = z.enum(['queued', 'running', 'succeeded', 'failed', 'canceled']);
export type JobStatus = z.infer<typeof zJobStatus>;

/** Human, warm progress copy — never a bare percentage. */
export const zJobStage = z.enum([
  'reading_the_room',
  'draping',
  'lighting',
  'developing',
  'reading_the_mood',
  'done',
]);
export type JobStage = z.infer<typeof zJobStage>;

export const JOB_STAGE_COPY: Record<JobStage, string> = {
  reading_the_room: 'Reading the room…',
  draping: 'Draping the fabric…',
  lighting: 'Setting the lights…',
  developing: 'Developing the shot…',
  reading_the_mood: 'Reading the mood…',
  done: 'Ready.',
};

/**
 * Why a job failed, when "it failed" is not a good enough answer.
 *
 *  error          — the studio glitched. The same input may well work.
 *  safety_blocked — the render could not be produced decently. The same input
 *                   will fail the same way, so the honest move is a different
 *                   garment or a different mood, not a retry.
 */
export const zJobFailureKind = z.enum(['error', 'safety_blocked']);
export type JobFailureKind = z.infer<typeof zJobFailureKind>;

export const zJob = z.object({
  id: zId,
  userId: zId,
  status: zJobStatus,
  stage: zJobStage,
  progress: z.number().min(0).max(100),
  lookId: zId.nullable().default(null),
  look: zLook.nullable().optional(),
  error: z.string().nullable().default(null),
  failureKind: zJobFailureKind.nullable().default(null),
  /**
   * Whether re-running the IDENTICAL input could succeed.
   *
   * `false` means a "try again" button is a lie — it would spend another
   * fitting to reach the same wall. Surfaces should offer a change of garment
   * or mood instead. Defaults to `true` so nothing that predates this field
   * loses its retry.
   */
  retryable: z.boolean().default(true),
  createdAt: zTimestamp,
  updatedAt: zTimestamp,
});
export type Job = z.infer<typeof zJob>;

/* ------------------------------------------------------------------ *
 * Notifications
 * ------------------------------------------------------------------ */

export const zNotification = z.object({
  id: zId,
  userId: zId,
  kind: z.enum(['hype', 'follow', 'room_invite', 'remix', 'job_done', 'drop_live', 'system']),
  actor: zPublicUser.nullable().default(null),
  lookId: zId.nullable().default(null),
  roomCode: z.string().nullable().default(null),
  text: z.string().max(200),
  read: z.boolean().default(false),
  createdAt: zTimestamp,
});
export type Notification = z.infer<typeof zNotification>;

/* ------------------------------------------------------------------ *
 * Stylist chat
 * ------------------------------------------------------------------ */

export const zChatMessage = z.object({
  id: zId,
  role: z.enum(['user', 'assistant']),
  content: z.string(),
  /** Garments the stylist pulled while answering — rendered as a mini rack. */
  suggestedGarmentIds: z.array(zId).max(8).default([]),
  createdAt: zTimestamp,
});
export type ChatMessage = z.infer<typeof zChatMessage>;

/* ================================================================== *
 * API request / response payloads
 * ================================================================== */

export const zRegisterRequest = z.object({
  email: zEmail,
  password: zPassword,
  username: zUsername,
  displayName: z.string().min(1).max(60),
});
export type RegisterRequest = z.infer<typeof zRegisterRequest>;

export const zLoginRequest = z.object({
  email: zEmail,
  password: z.string().min(1).max(200),
});
export type LoginRequest = z.infer<typeof zLoginRequest>;

export const zGoogleAuthRequest = z.object({
  /** Google ID token (JWT) from the client SDK / OAuth code exchange. */
  idToken: z.string().min(10),
  /** Only used the first time we see this Google account. */
  username: zUsername.optional(),
});
export type GoogleAuthRequest = z.infer<typeof zGoogleAuthRequest>;

export const zAuthSession = z.object({
  accessToken: z.string(),
  refreshToken: z.string(),
  expiresAt: zTimestamp,
  user: zUser,
});
export type AuthSession = z.infer<typeof zAuthSession>;

export const zRefreshRequest = z.object({ refreshToken: z.string().min(10) });
export type RefreshRequest = z.infer<typeof zRefreshRequest>;

export const zForgotPasswordRequest = z.object({ email: zEmail });
export type ForgotPasswordRequest = z.infer<typeof zForgotPasswordRequest>;

export const zResetPasswordRequest = z.object({
  /** The opaque token from the emailed link. Single use, 30 minutes. */
  token: z.string().min(1).max(256),
  password: zPassword,
});
export type ResetPasswordRequest = z.infer<typeof zResetPasswordRequest>;

export const zChangePasswordRequest = z.object({
  /** Not `zPassword` — an old password may predate the current rules. */
  currentPassword: z.string().min(1).max(200),
  newPassword: zPassword,
  /**
   * The caller's own refresh token. A password change revokes every *other*
   * session, and the server has no way to tell which stored token belongs to
   * the request otherwise — the access token carries no link to it. The client
   * fills this in from its token store; omit it and every session goes,
   * including this one.
   */
  keepRefreshToken: z.string().min(10).optional(),
});
export type ChangePasswordRequest = z.infer<typeof zChangePasswordRequest>;

/** Deliberately contentless: it must look identical for every email. */
export const zOkResponse = z.object({ ok: z.literal(true) });
export type OkResponse = z.infer<typeof zOkResponse>;

export const zUpdateMeRequest = z.object({
  displayName: z.string().min(1).max(60).optional(),
  username: zUsername.optional(),
  bio: z.string().max(240).optional(),
  signatureMood: zMoodId.nullable().optional(),
  era: z.string().max(40).nullable().optional(),
  avatarUrl: z.string().url().nullable().optional(),
  links: z.array(z.object({ label: z.string().max(30), url: z.string().url() })).max(5).optional(),
});
export type UpdateMeRequest = z.infer<typeof zUpdateMeRequest>;

export const zCreateBodyShotRequest = z.object({
  /** data: URL or base64 payload of the photo. */
  image: z.string().min(64),
  mimeType: z.string().default('image/jpeg'),
  label: z.string().max(40).optional(),
  makePrimary: z.boolean().default(false),
});
export type CreateBodyShotRequest = z.infer<typeof zCreateBodyShotRequest>;

export const zGarmentQuery = z.object({
  q: z.string().max(80).optional(),
  category: zGarmentCategory.optional(),
  mood: zMoodId.optional(),
  occasion: zOccasionId.optional(),
  dropId: zId.optional(),
  featured: z.coerce.boolean().optional(),
  sort: z.enum(['trending', 'new', 'name']).default('trending'),
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(60).default(24),
});
export type GarmentQuery = z.infer<typeof zGarmentQuery>;

export const zTryOnRequest = z.object({
  bodyShotId: zId,
  garmentIds: z.array(zId).min(1).max(6),
  mood: zMoodId.nullable().default(null),
  occasion: zOccasionId.nullable().default(null),
  /** Optional scene direction, e.g. "rooftop in lisbon at golden hour". */
  scene: z.string().max(200).default(''),
  /** Keep the original background instead of restyling the scene. */
  keepBackground: z.boolean().default(true),
  roomId: zId.nullable().default(null),
  visibility: zVisibility.default('private'),
  remixOfId: zId.nullable().default(null),
});
export type TryOnRequest = z.infer<typeof zTryOnRequest>;

export const zUpdateLookRequest = z.object({
  title: z.string().max(80).optional(),
  visibility: zVisibility.optional(),
  lookbookIds: z.array(zId).max(20).optional(),
});
export type UpdateLookRequest = z.infer<typeof zUpdateLookRequest>;

export const zHypeRequest = z.object({
  kind: zHypeKind,
  /** false removes the reaction. */
  on: z.boolean().default(true),
});
export type HypeRequest = z.infer<typeof zHypeRequest>;

export const zFeedQuery = z.object({
  scope: z.enum(['runway', 'following', 'mood']).default('runway'),
  mood: zMoodId.optional(),
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(40).default(16),
});
export type FeedQuery = z.infer<typeof zFeedQuery>;

export const zCreateRoomRequest = z.object({
  title: z.string().max(60).default('Fitting room'),
  prompt: z.string().max(140).default(''),
  lookIds: z.array(zId).max(20).default([]),
});
export type CreateRoomRequest = z.infer<typeof zCreateRoomRequest>;

export const zCreateLookbookRequest = z.object({
  title: z.string().min(1).max(60),
  subtitle: z.string().max(120).default(''),
  mood: zMoodId.nullable().default(null),
  visibility: zVisibility.default('private'),
  lookIds: z.array(zId).max(200).default([]),
});
export type CreateLookbookRequest = z.infer<typeof zCreateLookbookRequest>;

export const zUpdateLookbookRequest = zCreateLookbookRequest.partial().extend({
  coverLookId: zId.nullable().optional(),
});
export type UpdateLookbookRequest = z.infer<typeof zUpdateLookbookRequest>;

export const zStylistChatRequest = z.object({
  message: z.string().min(1).max(1000),
  history: z.array(z.object({ role: z.enum(['user', 'assistant']), content: z.string() })).max(20).default([]),
  /** Ground the stylist in a specific look the user is staring at. */
  lookId: zId.nullable().default(null),
  mood: zMoodId.nullable().default(null),
});
export type StylistChatRequest = z.infer<typeof zStylistChatRequest>;

export const zCaptionRequest = z.object({
  lookId: zId,
  /** How the caption should sound. */
  tone: z.enum(['deadpan', 'poetic', 'unhinged', 'soft', 'clean', 'funny']).default('deadpan'),
  /** Optional extra context, e.g. "posting this for my birthday". */
  context: z.string().max(200).default(''),
});
export type CaptionRequest = z.infer<typeof zCaptionRequest>;

export const zCaptionResponse = z.object({
  captions: z.array(z.string().max(220)).min(1).max(5),
  hashtags: z.array(z.string().max(30)).max(10),
});
export type CaptionResponse = z.infer<typeof zCaptionResponse>;

/* ------------------------------------------------------------------ *
 * Public counts
 * ------------------------------------------------------------------ */

/**
 * Live aggregates for the public site. Every field is counted from a DO at
 * request time — none of it is a constant, and none of it is per-user.
 *
 * Zero is a legitimate value and means "we have nothing to say yet". Callers
 * must render nothing rather than substituting a placeholder figure; a number
 * on this page is a measurement, and an invented measurement is a lie.
 */
export const zPlatformStats = z.object({
  /** Garments in the rack. */
  garments: z.number().int().nonnegative(),
  /** Published drops. */
  drops: z.number().int().nonnegative(),
  /** Looks on the public Runway. */
  publicLooks: z.number().int().nonnegative(),
  /** Size of the mood taxonomy. */
  moods: z.number().int().nonnegative(),
  /** Individual reactions sent on public looks. */
  hypeSent: z.number().int().nonnegative(),
});
export type PlatformStats = z.infer<typeof zPlatformStats>;

/* ------------------------------------------------------------------ *
 * Generic envelopes
 * ------------------------------------------------------------------ */

export function zPage<T extends z.ZodTypeAny>(item: T) {
  return z.object({
    items: z.array(item),
    cursor: z.string().nullable().default(null),
    hasMore: z.boolean().default(false),
  });
}

export interface Page<T> {
  items: T[];
  cursor: string | null;
  hasMore: boolean;
}

export const zApiError = z.object({
  error: z.object({
    code: z.string(),
    message: z.string(),
    /** Field-level messages for forms. */
    fields: z.record(z.string(), z.string()).optional(),
  }),
});
export type ApiErrorBody = z.infer<typeof zApiError>;

/* ================================================================== *
 * Generation records — evals
 *
 * Every call we make to a model writes one of these. It exists so that
 * "did that prompt change make things worse?" and "how often does the
 * image model return something it should not have?" are questions with
 * *answers* rather than opinions.
 *
 * PRIVACY, AND THIS IS THE WHOLE RULE: a record holds R2 **keys** and
 * metadata. It never holds image bytes, never holds a base64 payload,
 * and is never used as training data. Body shots stay exactly where the
 * user put them — one private R2 object — and this table only ever
 * points at them. See `apps/api/src/services/eval-log.ts`.
 * ================================================================== */

/** Every distinct thing we ask a model to do. One record per call. */
export const zGenerationKind = z.enum([
  'tryon',
  'garment_photo',
  'mood_read',
  'caption',
  'body_shot_assessment',
  'stylist',
]);
export type GenerationKind = z.infer<typeof zGenerationKind>;

export const GENERATION_KINDS = zGenerationKind.options;

export const GENERATION_KIND_COPY: Record<GenerationKind, string> = {
  tryon: 'Try-on',
  garment_photo: 'Garment packshot',
  mood_read: 'Mood read',
  caption: 'Caption',
  body_shot_assessment: 'Body-shot check',
  stylist: 'Savant',
};

/**
 * `safety_blocked` is split out from `failed` on purpose: it is the number the
 * safety work is judged by, and burying it inside a generic failure count is
 * exactly how you end up unable to tell a fix from a coincidence.
 */
export const zGenerationOutcome = z.enum(['ok', 'failed', 'safety_blocked']);
export type GenerationOutcome = z.infer<typeof zGenerationOutcome>;

export const GENERATION_OUTCOMES = zGenerationOutcome.options;

/**
 * The annotation taxonomy. These are this product's real failure modes, not a
 * generic quality rubric — `nudity` and `body_altered` are the two rails from
 * ARCHITECTURE §0, and the rest are the ways a try-on visibly goes wrong.
 */
export const zEvalLabel = z.enum([
  'nudity',
  'body_altered',
  'wrong_garment',
  'garment_recoloured',
  'bad_drape',
  'artefacts',
  'face_changed',
  'pose_changed',
  'great',
]);
export type EvalLabel = z.infer<typeof zEvalLabel>;

export const EVAL_LABELS = zEvalLabel.options;

export const EVAL_LABEL_COPY: Record<EvalLabel, string> = {
  nudity: 'Nudity',
  body_altered: 'Body altered',
  wrong_garment: 'Wrong garment',
  garment_recoloured: 'Garment recoloured',
  bad_drape: 'Bad drape',
  artefacts: 'Artefacts',
  face_changed: 'Face changed',
  pose_changed: 'Pose changed',
  great: 'Great',
};

/** Labels that mean the output should never have reached a user. */
export const EVAL_CRITICAL_LABELS: readonly EvalLabel[] = ['nudity', 'body_altered', 'face_changed'];

/**
 * Hard cap on any single stored string, in characters.
 *
 * Prompts run ~2-4k characters and a stylist reply can run long. 8000 keeps a
 * whole try-on prompt intact (which is the point — you cannot diff a prompt you
 * only half kept) while bounding one Durable Object row. Anything longer is cut
 * and flagged `truncated`, so a reader is never silently shown a partial prompt.
 */
export const EVAL_TEXT_LIMIT = 8000;

export const zGenerationInput = z.object({
  /** The exact prompt text sent to the model, capped at `EVAL_TEXT_LIMIT`. */
  prompt: z.string().default(''),
  /** Length of the prompt before capping, so truncation is measurable. */
  promptChars: z.number().int().nonnegative().default(0),
  promptTruncated: z.boolean().default(false),
  /**
   * R2 keys of every image handed to the model, in prompt order (for try-on:
   * body shot first, then garments — the order is load-bearing). Keys only.
   */
  imageKeys: z.array(z.string().max(200)).max(12).default([]),
  /** Broken out because it is the one private, personal input. */
  bodyShotKey: z.string().max(200).nullable().default(null),
  garmentIds: z.array(zId).max(8).default([]),
  mood: zMoodId.nullable().default(null),
  occasion: zOccasionId.nullable().default(null),
  scene: z.string().max(200).default(''),
  /** Drives which model tier ran. */
  plan: zPlan.nullable().default(null),
});
export type GenerationInput = z.infer<typeof zGenerationInput>;

export const zGenerationOutput = z.object({
  /** R2 key of the produced image, for image kinds. Never the bytes. */
  imageKey: z.string().max(200).nullable().default(null),
  imageMimeType: z.string().max(80).nullable().default(null),
  /** Size of the produced image in bytes. Metadata about the file, not the file. */
  imageBytes: z.number().int().nonnegative().nullable().default(null),
  /** Raw text, or the parsed JSON re-serialised, for text kinds. */
  text: z.string().default(''),
  textChars: z.number().int().nonnegative().default(0),
  textTruncated: z.boolean().default(false),
});
export type GenerationOutput = z.infer<typeof zGenerationOutput>;

/**
 * Operator judgement. Internal-only — never returned on any user-facing route.
 *
 * 1-5, not thumbs. A thumb answers "would I ship this", which the `outcome` and
 * the `nudity` label already answer. What a prompt change actually moves is the
 * middle of the distribution — mediocre drape becoming decent drape — and a
 * binary cannot show that. A mean over 1-5 per kind and per model does, and it
 * is the number that tells you a change regressed quality even when nothing
 * outright broke.
 */
export const zEvalAnnotation = z.object({
  rating: z.number().int().min(1).max(5).nullable().default(null),
  labels: z.array(zEvalLabel).max(EVAL_LABELS.length).default([]),
  note: z.string().max(2000).default(''),
  /** The operator who last touched it. */
  ratedBy: zId.nullable().default(null),
  ratedAt: zTimestamp.nullable().default(null),
});
export type EvalAnnotation = z.infer<typeof zEvalAnnotation>;

export const zGenerationRecord = z.object({
  id: zId,
  /**
   * Stable across every model call belonging to one user action. A try-on
   * shares a traceId between the packshots it generated, the image edit and
   * the mood read — so a bad look can be read back as the whole chain.
   */
  traceId: zId,
  kind: zGenerationKind,
  /** The model id as actually configured at call time, not the default. */
  model: z.string().max(120),
  userId: zId,
  outcome: zGenerationOutcome,
  /** The error message when the call did not succeed. */
  error: z.string().max(500).nullable().default(null),
  startedAt: zTimestamp,
  finishedAt: zTimestamp,
  latencyMs: z.number().int().nonnegative(),
  input: zGenerationInput,
  output: zGenerationOutput,
  annotation: zEvalAnnotation.nullable().default(null),
});
export type GenerationRecord = z.infer<typeof zGenerationRecord>;

/** A signed, short-lived URL for one of the record's R2 keys. Never persisted. */
export const zEvalMedia = z.object({
  key: z.string(),
  url: z.string(),
  /** `input` images versus the `output` image. */
  role: z.enum(['input', 'output']),
});
export type EvalMedia = z.infer<typeof zEvalMedia>;

/** What the eval routes actually return: a record plus viewable media. */
export const zEvalItem = zGenerationRecord.extend({
  media: z.array(zEvalMedia).default([]),
});
export type EvalItem = z.infer<typeof zEvalItem>;

/** A boolean that survives the round trip through a query string. */
const zQueryFlag = z.preprocess(
  (value) => (typeof value === 'string' ? value === 'true' || value === '1' : value),
  z.boolean(),
);

export const zEvalQuery = z.object({
  kind: zGenerationKind.optional(),
  outcome: zGenerationOutcome.optional(),
  /** Exact rating match, 1-5. */
  rating: z.coerce.number().int().min(1).max(5).optional(),
  label: zEvalLabel.optional(),
  model: z.string().max(120).optional(),
  traceId: zId.optional(),
  userId: zId.optional(),
  /** Inclusive lower / exclusive upper bound on `startedAt`, epoch ms. */
  since: z.coerce.number().int().nonnegative().optional(),
  until: z.coerce.number().int().nonnegative().optional(),
  /**
   * The annotation queue's default: only what nobody has rated yet.
   *
   * Not `z.coerce.boolean()` — that turns the string `"false"` into `true`,
   * which for this flag would mean "show me the unrated ones" every time the
   * studio explicitly asked for everything.
   */
  unratedOnly: zQueryFlag.optional(),
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(200).default(25),
});
export type EvalQuery = z.infer<typeof zEvalQuery>;

export const zEvalAnnotationRequest = z.object({
  rating: z.number().int().min(1).max(5).nullable().optional(),
  labels: z.array(zEvalLabel).max(EVAL_LABELS.length).optional(),
  note: z.string().max(2000).optional(),
});
export type EvalAnnotationRequest = z.infer<typeof zEvalAnnotationRequest>;

const zBreakdown = z.object({
  key: z.string(),
  count: z.number().int().nonnegative(),
  ok: z.number().int().nonnegative(),
  failed: z.number().int().nonnegative(),
  safetyBlocked: z.number().int().nonnegative(),
  rated: z.number().int().nonnegative(),
  /** `null` when nothing in this slice has been rated yet. Never 0-as-unknown. */
  meanRating: z.number().nullable(),
});
export type EvalBreakdown = z.infer<typeof zBreakdown>;

/**
 * The operator dashboard. `safetyBlockRate` is the headline: it is what moves
 * when a guardrail lands, and a guardrail whose effect you cannot see in this
 * number did not land.
 */
export const zEvalStats = z.object({
  total: z.number().int().nonnegative(),
  ok: z.number().int().nonnegative(),
  failed: z.number().int().nonnegative(),
  safetyBlocked: z.number().int().nonnegative(),
  /** safetyBlocked / total, 0 when there is nothing to divide. */
  safetyBlockRate: z.number().min(0).max(1),
  rated: z.number().int().nonnegative(),
  unrated: z.number().int().nonnegative(),
  meanRating: z.number().nullable(),
  byKind: z.array(zBreakdown).default([]),
  byModel: z.array(zBreakdown).default([]),
  labelCounts: z.record(zEvalLabel, z.number().int().nonnegative()).default({}),
  /** Bounds actually applied, echoed back so a chart cannot mislabel itself. */
  since: zTimestamp.nullable().default(null),
  until: zTimestamp.nullable().default(null),
});
export type EvalStats = z.infer<typeof zEvalStats>;
